import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { LoadingButton } from '@mui/lab';
import DoneIcon from '@mui/icons-material/Done';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Button, Typography } from '@mui/material';
import { useFormik } from 'formik';
import useGlobal from 'global-state/store';
import { dynamicUnitName } from 'components/units/UnitDefinitionsAndMasks';
import { fillingStatusFromValue } from 'components/utils/fillingType';
import { shouldShowFillingActionInMainScreen } from 'components/utils/fillingActionPlacement';
import useMenu from 'menu-actions/useMenu';
import InventoryItem from '../InventoryItem';
import EditableField from '../EditableField';
import decimalsMask from '../DecimalsMask';
import { getDecimalPrecision } from '../getDecimalPrecision';
import { getDefaultFormValues, WeighedFormValues } from './getDefaultFormValues';
import { LabelInputSelect, NumberInput } from '../BoxetteInputs';
import validationSchemaWeighed from './ValidationSchemaWeighed';
import { validateFieldsAtSubmit } from './validateFieldsAtSubmit';
import ChangeFillingDialog from '../ChangeFillingDialog';
import ChangeFillingToggle from '../ChangeFillingToggle';
import CustomOperatorFieldsInput from '../CustomOperatorFieldsInput';
import OperationNavigationBar from '../components/OperationNavigationBar';

type Props = {
  itemData: Record<string, unknown>;
  mappingConfig: {
    columns: Record<string, any>;
    customFields: string[];
    displayStyles: Record<string, string>;
  };
  recordOperation: (values: WeighedFormValues, proceedToNext: boolean) => Promise<void>;
  nextOperation: () => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPreviousContainer: () => void;
  onNextContainer: () => void;
  containerSearchOptions: {
    key: string;
    label: string;
    description: string;
    container: Record<string, unknown>;
    searchText: string;
  }[];
  onSearchContainer: (value: string) => void;
  onSelectContainer: (option: {
    key: string;
    label: string;
    description: string;
    container: Record<string, unknown>;
    searchText: string;
  }) => void;
  inventoryData: { id: string; containersArray?: unknown[]; mappingConfig?: any };
  setInventoryData: (inv: unknown) => void;
  previousItemFormValues: Partial<WeighedFormValues> | undefined;
};

export default function WeighedContainerOperation({
  itemData,
  mappingConfig,
  recordOperation,
  nextOperation,
  canGoPrevious,
  canGoNext,
  onPreviousContainer,
  onNextContainer,
  containerSearchOptions,
  inventoryData,
  setInventoryData,
  onSearchContainer,
  onSelectContainer,
  previousItemFormValues,
}: Props) {
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const { dispatchMenuActions } = useMenu();
  const [openChangeFilling, setOpenChangeFilling] = useState(false);
  const showChangeFillingInMainScreen = shouldShowFillingActionInMainScreen(mappingConfig);

  // Helper function to get display style for a field
  const getFieldDisplayStyle = (fieldName: string) => {
    const fieldConfig = mappingConfig.columns[fieldName];
    if (fieldConfig.displayStyle) {
      return fieldConfig.displayStyle;
    }
    // Container info fields default to editableText
    const containerInfoFields = ['totalCapacity', 'usableCapacity'];
    if (containerInfoFields.includes(fieldName)) {
      return 'editableText';
    }
    // All other fields default to inputField
    return 'inputField';
  };

  // Helper function to check if field should be hidden
  const isFieldHidden = (fieldName: string) => getFieldDisplayStyle(fieldName) === 'hidden';

  const initialValues: WeighedFormValues = getDefaultFormValues(
    itemData,
    mappingConfig.columns as any,
    mappingConfig.customFields as any,
    previousItemFormValues,
  );

  const submitRecord = async (values: WeighedFormValues, proceedToNext: boolean) => {
    try {
      await recordOperation(values, proceedToNext);
    } catch (_) {
      // useOperation reports the save failure. Keep the form available for retry.
    }
  };

  const formik = useFormik<WeighedFormValues>({
    initialValues,
    validationSchema: validationSchemaWeighed(t, mappingConfig),
    onSubmit: async (values) => {
      const fillingStatus = fillingStatusFromValue(
        (itemData as Record<string, unknown>)[mappingConfig.columns.filling.mapping.csvName] as string,
      );
      if (await validateFieldsAtSubmit(
        values,
        mappingConfig.columns,
        fillingStatus,
        globalActions,
        t,
        mappingConfig.displayStyles,
      )) {
        return submitRecord(values, false);
      }
      return undefined;
    },
  });

  const handleSubmitAndNext = async () => {
    const fillingStatus = fillingStatusFromValue(
      (itemData as Record<string, unknown>)[mappingConfig.columns.filling.mapping.csvName] as string,
    );
    if (await validateFieldsAtSubmit(
      formik.values,
      mappingConfig.columns,
      fillingStatus,
      globalActions,
      t,
      mappingConfig.displayStyles,
    )) {
      await submitRecord(formik.values, true);
    }
  };

  const { abvStatus, volumeStatus } = itemData as { abvStatus?: string; volumeStatus?: string };
  const fillingStatus = fillingStatusFromValue(
    (itemData as Record<string, unknown>)[mappingConfig.columns.filling.mapping.csvName] as string,
  );
  const isEmpty = fillingStatus === 'empty';

  const changeFillingAction = useCallback(() => ({
    name: t('operation.modify_filling'),
    callback: () => setOpenChangeFilling(true),
  }), [t]);

  useEffect(() => {
    const action = changeFillingAction();
    dispatchMenuActions({ type: 'add', actions: [action] });
    return () => dispatchMenuActions({ type: 'remove', names: [action.name] });
  }, [dispatchMenuActions, changeFillingAction]);

  // If container is empty, set mass to containerMass (if provided)
  useEffect(() => {
    if (isEmpty) {
      const containerMassVal = formik.values.containerMass?.value;
      if (containerMassVal !== undefined && containerMassVal !== null && containerMassVal !== '') {
        formik.setFieldValue('mass.value', containerMassVal, false);
        formik.setFieldValue('mass.unit', formik.values.containerMass.unit, false);
      } else {
        formik.setFieldValue('mass.value', '', false);
        formik.setFieldValue('mass.unit', 'kg', false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty, formik.values.containerMass?.value, formik.values.containerMass?.unit]);

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'stretch',
        gap: 1,
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 600,
      }}
    >
      <Paper elevation={0} sx={{ px: 3, py: 2 }}>
        <InventoryItem
          onClick={undefined}
          itemData={itemData}
          columnMappings={mappingConfig.columns}
          showFillingLabel
          containerABVStatus={abvStatus}
          containerVolumeStatus={volumeStatus}
        />
      </Paper>
      {showChangeFillingInMainScreen && (
        <ChangeFillingToggle
          inventoryData={inventoryData}
          itemData={itemData}
          setInventoryData={setInventoryData}
        />
      )}

      {/* Volume section: mass + containerMass */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, md: 3 },
          py: 2,
          border: 1,
          borderColor: 'secondary.main',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            position: 'absolute',
            top: 2,
            right: 10,
          }}
        >
          Volume
        </Typography>

        {/* mass input */}
        {!isFieldHidden('mass') && (
          <LabelInputSelect
            label={<Typography component="span">{t('barrel_inventory.column_options.mass')}</Typography>}
            input={(
              <NumberInput
                id="mass.value"
                name="mass.value"
                disabled={isEmpty}
                onValueChangeCallback={(value: number) => formik.setFieldValue('mass.value', value)}
                placeholder="ex: 235"
                value={formik.values.mass.value as number}
                decimalScale={getDecimalPrecision('mass', formik.values.mass.unit, mappingConfig)}
                allowNegative
                onBlur={formik.handleBlur}
              />
            )}
            error={
              formik.touched.mass?.value
              && (formik.errors as unknown as { mass?: { value?: string } }).mass?.value
              && (
                <Typography component="span" style={{ fontSize: 13, color: 'red' }}>
                  {(formik.errors as unknown as { mass?: { value?: string } }).mass?.value as string}
                </Typography>
              )
            }
            select={(
              <Typography component="span" sx={{ p: 2 }}>
                {dynamicUnitName(formik.values.mass.unit)}
              </Typography>
            )}
          />
        )}

        {/* containerMass editable */}
        {!isFieldHidden('containerMass') && (
          <EditableField
            formik={formik}
            fieldKey="containerMass"
            translationKey="barrel_inventory.column_options.containerMass"
            dynamicUnitName={dynamicUnitName}
            decimalsMask={decimalsMask}
            displayStyle={getFieldDisplayStyle('containerMass')}
            mappingConfig={mappingConfig}
          />
        )}
      </Paper>

      {/* ABV section: volumetricMass */}
      <Paper
        elevation={0}
        sx={{
          px: { xs: 1, md: 3 },
          py: 2,
          border: 1,
          borderColor: 'ourOrange.main',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            color: 'ourOrange.main',
            position: 'absolute',
            top: 2,
            right: 10,
          }}
        >
          {t('inventory_item.status_caption_label.abvStatus')}
        </Typography>

        {/* volumetricMass input */}
        {!isFieldHidden('volumetricMass') && (
          <LabelInputSelect
            label={<Typography component="span">{t('barrel_inventory.column_options.volumetricMass')}</Typography>}
            input={(
              <NumberInput
                id="volumetricMass.value"
                name="volumetricMass.value"
                disabled={isEmpty}
                onValueChangeCallback={(value: number) => formik.setFieldValue('volumetricMass.value', value)}
                placeholder="ex: 990"
                value={formik.values.volumetricMass.value as number}
                decimalScale={getDecimalPrecision('volumetricMass', formik.values.volumetricMass.unit, mappingConfig)}
                allowNegative
                onBlur={formik.handleBlur}
              />
            )}
            error={
              formik.touched.volumetricMass?.value
              && (formik.errors as unknown as { volumetricMass?: { value?: string } }).volumetricMass?.value
              && (
                <Typography component="span" style={{ fontSize: 13, color: 'red' }}>
                  {(formik.errors as unknown as { volumetricMass?: { value?: string } })
                    .volumetricMass?.value as string}
                </Typography>
              )
            }
            select={(
              <Typography component="span" sx={{ p: 2 }}>
                {dynamicUnitName(formik.values.volumetricMass.unit)}
              </Typography>
            )}
          />
        )}
      </Paper>

      <CustomOperatorFieldsInput
        mappingConfig={mappingConfig}
        formik={formik}
        itemData={itemData}
      />

      {!isEmpty && (
        <LoadingButton
          variant="contained"
          onClick={handleSubmitAndNext}
          endIcon={<ArrowForwardIosIcon />}
        >
          {t('inventory_item.record_and_continue')}
        </LoadingButton>
      )}

      {!isEmpty && (
        <LoadingButton
          variant="contained"
          type="submit"
          endIcon={<DoneIcon />}
        >
          {t('validate')}
        </LoadingButton>
      )}

      {isEmpty && (
        <Button
          onClick={handleSubmitAndNext}
          variant="contained"
          endIcon={<ArrowForwardIosIcon />}
        >
          {t('inventory_item.validate_and_continue')}
        </Button>
      )}
      {isEmpty && (
        <LoadingButton
          variant="contained"
          type="submit"
          endIcon={<DoneIcon />}
        >
          {t('validate')}
        </LoadingButton>
      )}

      <OperationNavigationBar
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onPrevious={onPreviousContainer}
        onNext={onNextContainer}
        options={containerSearchOptions}
        onSearchContainer={onSearchContainer}
        onSelectContainer={onSelectContainer}
      />

      {inventoryData && setInventoryData && (
        <ChangeFillingDialog
          open={openChangeFilling}
          setOpen={setOpenChangeFilling}
          itemData={itemData}
          setInventoryData={setInventoryData}
          inventoryData={inventoryData}
        />
      )}
    </Box>
  );
}
